/**
 * Optimizer Control Center — list card: paste bar, table, edit drawer, empty states
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState, type FC } from 'react';
import { AlertTriangle, Gauge } from 'lucide-react';
import { trackerLabel } from '@trafficpulse/shared';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LoadingSpinner,
} from '@/components/ui';
import FlowOptimizerDrawer from '@/features/flow-optimizer/FlowOptimizerDrawer';
import type { FlowOptimizerProvider } from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerRowKey, useOptimizerClipboard } from '@/features/optimizer-center/clipboard';
import { PasteConfigBar } from '@/features/optimizer-center/clipboard/ui';
import { BulkAssignBar } from '@/features/flow-optimizer-library/scheduler';
import { OptimizerTable } from '@/features/optimizer-center/components/OptimizerTable';
import { useAutoOpenRow } from '@/features/optimizer-center/deep-link';
import { useCountdownTick } from '@/features/optimizer-center/hooks/useCountdownTick';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { joinWithAnd, trackerText } from '@/features/optimizer-center/tracker-status';
import { useAuth } from '@/hooks';

export interface OptimizerTableCardProps {
  rows: OptimizerRow[];
  isLoading: boolean;
  failedProviders: FlowOptimizerProvider[];
  loadingProviders: FlowOptimizerProvider[];
  autoOpenFlow?: string | null;
  autoOpenProvider?: FlowOptimizerProvider;
}

export const OptimizerTableCard: FC<OptimizerTableCardProps> = ({
  rows,
  isLoading,
  failedProviders,
  loadingProviders,
  autoOpenFlow, autoOpenProvider,
}) => {
  const { hasPermission } = useAuth();
  const canEditSettings = hasPermission('flows.edit_settings');
  const nowMs = useCountdownTick(rows.length > 0);
  const [editRow, setEditRow] = useState<OptimizerRow | null>(null);
  // Arriving from the Banks Flow Tree: same drawer the Edit button opens.
  useAutoOpenRow(rows, autoOpenFlow ?? null, setEditRow, autoOpenProvider);
  const clip = useOptimizerClipboard();

  const selectableKeys = useMemo(
    () => rows.filter((r) => optimizerRowKey(r) !== clip.copySourceKey).map(optimizerRowKey),
    [rows, clip.copySourceKey],
  );
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => clip.selected.has(k));
  const someSelected = clip.selected.size > 0 && !allSelected;
  const targets = useMemo(
    () => rows.filter((r) => clip.selected.has(optimizerRowKey(r))),
    [rows, clip.selected],
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 border-b py-4">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Gauge className="h-4 w-4 text-muted-foreground" />
          Optimized flows
        </CardTitle>
        {!isLoading && (
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {rows.length} {rows.length === 1 ? 'flow' : 'flows'}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {canEditSettings && clip.copied && (
          <PasteConfigBar
            copied={clip.copied}
            selectedCount={clip.selected.size}
            isPasting={clip.isPasting}
            onPaste={(fields) => clip.paste(targets, fields)}
            onClearSelection={clip.clearSelection}
            onClearCopied={clip.clearCopied}
          />
        )}
        {canEditSettings && (
          <BulkAssignBar targets={targets} onClearSelection={clip.clearSelection} />
        )}
        {failedProviders.length > 0 && (
          <div className="flex items-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2.5 text-sm text-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Could not load {joinWithAnd(failedProviders.map(trackerText))} configs — showing the rest.
          </div>
        )}
        {!isLoading && loadingProviders.length > 0 && (
          <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
            <LoadingSpinner size="xs" />
            Loading {joinWithAnd(loadingProviders.map(trackerLabel))} configs…
          </div>
        )}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="md" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            No optimizer configs match the current filters.
          </p>
        ) : (
          <OptimizerTable
            rows={rows}
            nowMs={nowMs}
            canEditSettings={canEditSettings}
            onEdit={setEditRow}
            selectionEnabled={canEditSettings}
            selected={clip.selected}
            copySourceKey={clip.copySourceKey}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleSelectAll={() =>
              allSelected ? clip.clearSelection() : clip.selectAll(selectableKeys)
            }
            onToggleSelect={clip.toggle}
            onCopy={clip.copy}
          />
        )}
        {editRow && (
          <FlowOptimizerDrawer
            campaignId={editRow.campaignId}
            flowId={editRow.flowId}
            campaignName={editRow.campaignName}
            flowName={editRow.flowName}
            provider={editRow.provider}
            open
            onOpenChange={(open) => {
              if (!open) setEditRow(null);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};
